/**
 * Email Campaigns Analytics Page
 * Industry-standard email marketing analytics and insights
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Mail,
  TrendingUp,
  Users,
  MousePointer,
  Eye,
  Target,
  Calendar,
  Award,
  RefreshCw,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '@services/api';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CampaignDetailsModal } from './CampaignDetailsModal';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import { CustomAutocomplete } from '@components/analytics/CustomAutocomplete';

interface CampaignMetrics {
  campaign_id: string;
  campaign_name: string;
  send_date: string;
  total_recipients: number;
  unique_opens: number;
  unique_clicks: number;
  bounces: number;
  unsubscribes: number;
  open_rate: number;
  click_rate: number;
  click_to_open_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
  engagement_score: number;
  conversion_rate?: number; // NEW: Conversion tracking
  total_conversions?: number;
  total_revenue?: number;
}

interface TimeSeriesData {
  date: string;
  opens: number;
  clicks: number;
  conversions: number;
}

interface UserSegment {
  segment: string;
  count: number;
  open_rate: number;
  click_rate: number;
}

const EmailCampaignsPage = () => {
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();
  const { companyId, dataSourceIds } = getSelection();
  
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignMetrics | null>(null);

  // Date range state using DateRangeFilter (default: Last 30 days)
  const getDefaultDateRange = (): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 30 days',
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('emailCampaigns_dateRange');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultDateRange();
      }
    }
    return getDefaultDateRange();
  });

  const [showFilters, setShowFilters] = useState(() => {
    const saved = localStorage.getItem('emailCampaigns_showFilters');
    return saved ? JSON.parse(saved) : false;
  });

  // Filter states with localStorage persistence
  const [campaignNameFilter, setCampaignNameFilter] = useState(() => {
    return localStorage.getItem('emailCampaigns_campaignNameFilter') || '';
  });
  const [minRecipientsFilter, setMinRecipientsFilter] = useState(() => {
    return localStorage.getItem('emailCampaigns_minRecipientsFilter') || '';
  });
  const [minOpenRateFilter, setMinOpenRateFilter] = useState(() => {
    return localStorage.getItem('emailCampaigns_minOpenRateFilter') || '';
  });

  const [configSetFilter, setConfigSetFilter] = useState(() => {
    return localStorage.getItem('emailCampaigns_configSetFilter') || '';
  });

  const [campaignIdFilter, setCampaignIdFilter] = useState(() => {
    return localStorage.getItem('emailCampaigns_campaignIdFilter') || '';
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('emailCampaigns_dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('emailCampaigns_showFilters', JSON.stringify(showFilters));
  }, [showFilters]);

  useEffect(() => {
    localStorage.setItem('emailCampaigns_campaignNameFilter', campaignNameFilter);
  }, [campaignNameFilter]);

  useEffect(() => {
    localStorage.setItem('emailCampaigns_minRecipientsFilter', minRecipientsFilter);
  }, [minRecipientsFilter]);

  useEffect(() => {
    localStorage.setItem('emailCampaigns_minOpenRateFilter', minOpenRateFilter);
  }, [minOpenRateFilter]);

  useEffect(() => {
    localStorage.setItem('emailCampaigns_configSetFilter', configSetFilter);
  }, [configSetFilter]);

  useEffect(() => {
    localStorage.setItem('emailCampaigns_campaignIdFilter', campaignIdFilter);
  }, [campaignIdFilter]);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<keyof CampaignMetrics>('send_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Show 20 campaigns per page

  // Handle column sort
  const handleSort = (column: keyof CampaignMetrics) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - default to descending
      setSortColumn(column);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Fetch campaign metrics
  const {
    data: metricsData,
    isLoading: loadingMetrics,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['email-campaigns', 'metrics', companyId, dataSourceIds, dateRange],
    queryFn: async () => {
      const response = await api.get('/api/v1/email-campaigns/metrics', {
        params: {
          companyId,
          dataSourceIds: dataSourceIds.join(','),
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
      });
      return response.data.data;
    },
    enabled: !!companyId && dataSourceIds.length > 0,
  });

  const campaigns: CampaignMetrics[] = metricsData?.campaigns || [];
  
  // Apply filters to get the current filtered set for cascading dropdowns
  const getFilteredCampaigns = () => {
    return campaigns.filter(campaign => {
      // Configuration Set filter
      if (configSetFilter && !campaign.campaign_id.toLowerCase().includes(configSetFilter.toLowerCase())) {
        return false;
      }
      
      // Campaign Name filter
      if (campaignNameFilter && !campaign.campaign_name.toLowerCase().includes(campaignNameFilter.toLowerCase())) {
        return false;
      }
      
      // Campaign ID filter
      if (campaignIdFilter && !campaign.campaign_id.toLowerCase().includes(campaignIdFilter.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  };
  
  const filteredForDropdowns = getFilteredCampaigns();
  
  // Extract unique values for autocomplete dropdowns from FILTERED campaigns
  // This creates cascading filters - each dropdown shows only values relevant to current filters
  const uniqueCampaignNames = Array.from(
    new Set(filteredForDropdowns.map(c => c.campaign_name))
  ).sort();
  
  // Extract unique configuration sets from campaign_id
  // Campaign IDs can have formats like:
  // - "emailoctopus-92ac08625b86fcb74e8dc6e633188d55-Campaign Name"
  // - "delivery-tracking"
  // - "unknown-Campaign Name"
  const uniqueConfigSets = Array.from(
    new Set(
      // Use ALL campaigns for config set (not filtered) - this is the first filter
      campaigns
        .map(c => {
          const id = c.campaign_id;
          
          // If it contains a long hash (32+ hex chars), extract provider-hash
          const hashMatch = id.match(/^([a-z]+-[a-f0-9]{32,})/i);
          if (hashMatch) {
            return hashMatch[1];
          }
          
          // Otherwise, extract first part before dash-emoji/dash-capital letter
          const parts = id.split('-');
          if (parts.length >= 2) {
            // Return first two parts (e.g., "delivery-tracking")
            return parts.slice(0, 2).join('-');
          }
          
          // Fallback: use first part only
          return parts[0] || id;
        })
        .filter((v, i, a) => v && a.indexOf(v) === i) // Unique, non-empty
    )
  ).sort();
  
  const uniqueCampaignIds = Array.from(
    new Set(filteredForDropdowns.map(c => c.campaign_id))
  ).sort();
  
  // Debug: Log extracted config sets
  if (uniqueConfigSets.length > 0) {
    console.log('📊 Extracted Configuration Sets:', uniqueConfigSets);
  }
  
  // Apply ALL filters for final table display
  const filteredCampaigns = campaigns.filter(campaign => {
    // Campaign name filter (case-insensitive)
    if (campaignNameFilter && !campaign.campaign_name.toLowerCase().includes(campaignNameFilter.toLowerCase())) {
      return false;
    }
    
    // Min recipients filter
    if (minRecipientsFilter && campaign.total_recipients < parseInt(minRecipientsFilter)) {
      return false;
    }
    
    // Min open rate filter
    if (minOpenRateFilter && campaign.open_rate < parseFloat(minOpenRateFilter)) {
      return false;
    }

    // Configuration Set filter (searches in campaign_id which contains config set)
    if (configSetFilter && !campaign.campaign_id.toLowerCase().includes(configSetFilter.toLowerCase())) {
      return false;
    }

    // Campaign ID filter (exact or partial match)
    if (campaignIdFilter && !campaign.campaign_id.toLowerCase().includes(campaignIdFilter.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Sort filtered campaigns
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = 0;
    if (bValue === null || bValue === undefined) bValue = 0;

    // Handle date sorting
    if (sortColumn === 'send_date') {
      aValue = new Date(a.send_date).getTime();
      bValue = new Date(b.send_date).getTime();
    }

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Handle number sorting
    const numA = Number(aValue);
    const numB = Number(bValue);
    return sortDirection === 'asc' ? numA - numB : numB - numA;
  });

  // Paginate sorted campaigns
  const totalPages = Math.ceil(sortedCampaigns.length / itemsPerPage);
  const paginatedCampaigns = sortedCampaigns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate overall metrics from FILTERED campaigns (not backend's overall)
  const overallMetrics = filteredCampaigns.length > 0 ? {
    total_campaigns: filteredCampaigns.length,
    total_recipients: filteredCampaigns.reduce((sum, c) => sum + c.total_recipients, 0),
    total_opens: filteredCampaigns.reduce((sum, c) => sum + c.unique_opens, 0),
    total_clicks: filteredCampaigns.reduce((sum, c) => sum + c.unique_clicks, 0),
    avg_open_rate: filteredCampaigns.reduce((sum, c) => sum + c.open_rate, 0) / filteredCampaigns.length,
    avg_click_rate: filteredCampaigns.reduce((sum, c) => sum + c.click_rate, 0) / filteredCampaigns.length,
    avg_engagement_score: filteredCampaigns.reduce((sum, c) => sum + c.engagement_score, 0) / filteredCampaigns.length,
  } : {
    total_campaigns: 0,
    total_recipients: 0,
    total_opens: 0,
    total_clicks: 0,
    avg_open_rate: 0,
    avg_click_rate: 0,
    avg_engagement_score: 0,
  };

  // Colors for charts
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Sortable header component
  const SortableHeader = ({ column, children, align = 'left' }: { 
    column: keyof CampaignMetrics; 
    children: React.ReactNode;
    align?: 'left' | 'right';
  }) => (
    <th 
      className={`px-6 py-3 text-${align} text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors select-none`}
      onClick={() => handleSort(column)}
    >
      <div className={`flex items-center gap-2 ${align === 'right' ? 'justify-end' : ''}`}>
        {children}
        {sortColumn === column && (
          sortDirection === 'asc' ? 
            <ArrowUp className="w-4 h-4" /> : 
            <ArrowDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-950">
      {/* Header */}
      <div className="bg-white dark:bg-secondary-900 border-b border-secondary-200 dark:border-secondary-800 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
              <Mail className="w-7 h-7 text-primary-600" />
              Email Campaign Analytics
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Track performance, engagement, and ROI of your email campaigns
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => refetchMetrics()}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button className="btn btn-primary flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export Report
            </button>
          </div>
        </div>

        {/* Date Range & Filters */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>

              {/* Advanced Filters Panel */}
              {showFilters && (
                <div className="mt-4 p-4 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              
              {/* 1. Configuration Set - FIRST */}
              <CustomAutocomplete
                label="Configuration Set"
                value={configSetFilter}
                onChange={setConfigSetFilter}
                options={uniqueConfigSets}
                placeholder="Select config set..."
              />
              
              {/* 2. Campaign Name - SECOND */}
              <CustomAutocomplete
                label="Campaign Name"
                value={campaignNameFilter}
                onChange={setCampaignNameFilter}
                options={uniqueCampaignNames}
                placeholder="Search campaigns..."
              />
              
              {/* 3. Campaign ID - THIRD */}
              <CustomAutocomplete
                label="Campaign ID"
                value={campaignIdFilter}
                onChange={setCampaignIdFilter}
                options={uniqueCampaignIds}
                placeholder="Select campaign ID..."
              />
              
              {/* 4. Min Recipients - FOURTH */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Min Recipients
                </label>
                <input
                  type="number"
                  placeholder="e.g., 100"
                  className="input"
                  value={minRecipientsFilter}
                  onChange={(e) => setMinRecipientsFilter(e.target.value)}
                />
              </div>
              
              {/* 5. Min Open Rate - FIFTH */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Min Open Rate (%)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 20"
                  className="input"
                  value={minOpenRateFilter}
                  onChange={(e) => setMinOpenRateFilter(e.target.value)}
                />
              </div>
            </div>
            
            {/* Filter Summary & Clear */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing <strong>{filteredCampaigns.length}</strong> of <strong>{campaigns.length}</strong> campaigns
              </div>
              {(campaignNameFilter || minRecipientsFilter || minOpenRateFilter || configSetFilter || campaignIdFilter) && (
                <button
                  onClick={() => {
                    setCampaignNameFilter('');
                    setMinRecipientsFilter('');
                    setMinOpenRateFilter('');
                    setConfigSetFilter('');
                    setCampaignIdFilter('');
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Campaigns */}
          <div className="bg-white dark:bg-secondary-900 rounded-lg p-6 shadow-sm border border-secondary-200 dark:border-secondary-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Total Campaigns
                </p>
                <p className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
                  {filteredCampaigns.length}
                </p>
                <p className="text-sm text-secondary-500 mt-1">
                  {filteredCampaigns.length !== campaigns.length && (
                    <span className="text-primary-600 dark:text-primary-400">
                      {campaigns.length} total
                    </span>
                  )}
                  {filteredCampaigns.length === campaigns.length && (
                    <span>All campaigns</span>
                  )}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          {/* Average Open Rate */}
          <div className="bg-white dark:bg-secondary-900 rounded-lg p-6 shadow-sm border border-secondary-200 dark:border-secondary-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Avg Open Rate
                </p>
                <p className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
                  {overallMetrics.avg_open_rate?.toFixed(1)}%
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Industry avg: 21%
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          {/* Average Click Rate */}
          <div className="bg-white dark:bg-secondary-900 rounded-lg p-6 shadow-sm border border-secondary-200 dark:border-secondary-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Avg Click Rate
                </p>
                <p className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
                  {overallMetrics.avg_click_rate?.toFixed(1)}%
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Industry avg: 2.6%
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                <MousePointer className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>

          {/* Engagement Score */}
          <div className="bg-white dark:bg-secondary-900 rounded-lg p-6 shadow-sm border border-secondary-200 dark:border-secondary-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Engagement Score
                </p>
                <p className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
                  {overallMetrics.avg_engagement_score?.toFixed(0)}
                </p>
                <p className="text-sm text-secondary-500 mt-1">Out of 100</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns Table */}
        <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-800 overflow-hidden">
          <div className="p-6 border-b border-secondary-200 dark:border-secondary-800">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-primary-600" />
              Campaign Performance
            </h2>
          </div>

          {loadingMetrics ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-600 border-t-transparent"></div>
              <p className="text-secondary-600 dark:text-secondary-400 mt-4">
                Loading campaigns...
              </p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="w-16 h-16 text-secondary-300 dark:text-secondary-700 mx-auto mb-4" />
              <p className="text-secondary-600 dark:text-secondary-400">
                No email campaign data found.
              </p>
              {(configSetFilter || campaignNameFilter || campaignIdFilter || minRecipientsFilter || minOpenRateFilter) ? (
                <div className="mt-4">
                  <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                    📊 Active filters may be hiding results. Try clearing them.
                  </p>
                  <button
                    onClick={() => {
                      setConfigSetFilter('');
                      setCampaignNameFilter('');
                      setCampaignIdFilter('');
                      setMinRecipientsFilter('');
                      setMinOpenRateFilter('');
                    }}
                    className="btn btn-primary"
                  >
                    Clear All Filters
                  </button>
                </div>
              ) : (
                <p className="text-sm text-secondary-500 mt-2">
                  Upload campaign data or check your data source configuration.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50 dark:bg-secondary-800">
                  <tr>
                    <SortableHeader column="campaign_name">Campaign</SortableHeader>
                    <SortableHeader column="send_date">Send Date</SortableHeader>
                    <SortableHeader column="total_recipients" align="right">Recipients</SortableHeader>
                    <SortableHeader column="open_rate" align="right">Open Rate</SortableHeader>
                    <SortableHeader column="click_rate" align="right">Click Rate</SortableHeader>
                    <SortableHeader column="click_to_open_rate" align="right">CTOR</SortableHeader>
                    <SortableHeader column="total_conversions" align="right">Conversions</SortableHeader>
                    <SortableHeader column="conversion_rate" align="right">Conv Rate</SortableHeader>
                    <SortableHeader column="bounce_rate" align="right">Bounce Rate</SortableHeader>
                    <SortableHeader column="unsubscribe_rate" align="right">Unsub Rate</SortableHeader>
                    <SortableHeader column="engagement_score" align="right">Engagement</SortableHeader>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 dark:divide-secondary-800">
                  {paginatedCampaigns.map((campaign, index) => (
                    <tr
                      key={campaign.campaign_id}
                      onClick={() => setSelectedCampaign(campaign)}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">
                            {campaign.campaign_name}
                          </p>
                          <p className="text-xs text-secondary-500">{campaign.campaign_id}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">
                          <div className="font-medium text-secondary-900 dark:text-white">
                            {new Date(campaign.send_date).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-secondary-500">
                            {new Date(campaign.send_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-secondary-900 dark:text-white">
                        {campaign.total_recipients.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <span
                          className={`font-medium ${
                            campaign.open_rate > 25
                              ? 'text-green-600'
                              : campaign.open_rate > 15
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {campaign.open_rate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <span
                          className={`font-medium ${
                            campaign.click_rate > 3
                              ? 'text-green-600'
                              : campaign.click_rate > 1.5
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {campaign.click_rate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-medium text-secondary-900 dark:text-white">
                        {campaign.click_to_open_rate.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-medium text-secondary-900 dark:text-white">
                        {campaign.total_conversions || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                            <span
                              className={`font-medium ${
                                campaign.conversion_rate !== null && campaign.conversion_rate !== undefined && campaign.conversion_rate > 5
                                  ? 'text-green-600'
                                  : campaign.conversion_rate !== null && campaign.conversion_rate !== undefined && campaign.conversion_rate > 2
                                  ? 'text-yellow-600'
                                  : campaign.conversion_rate !== null && campaign.conversion_rate !== undefined && campaign.conversion_rate > 0
                                  ? 'text-orange-600'
                                  : 'text-secondary-400'
                              }`}
                            >
                              {campaign.conversion_rate !== null && campaign.conversion_rate !== undefined ? campaign.conversion_rate.toFixed(1) + '%' : '0%'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {campaign.bounce_rate > 0 ? (
                          <span
                            className={`font-medium ${
                              campaign.bounce_rate < 0.5
                                ? 'text-green-600'
                                : campaign.bounce_rate < 2
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}
                          >
                            {campaign.bounce_rate.toFixed(2)}%
                          </span>
                        ) : (
                          <span className="text-xs text-secondary-400">0.00%</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {campaign.unsubscribe_rate > 0 ? (
                          <span
                            className={`font-medium ${
                              campaign.unsubscribe_rate < 0.2
                                ? 'text-green-600'
                                : campaign.unsubscribe_rate < 0.5
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}
                          >
                            {campaign.unsubscribe_rate.toFixed(2)}%
                          </span>
                        ) : (
                          <span className="text-xs text-secondary-400">0.00%</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                          {campaign.engagement_score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-800 flex items-center justify-between">
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">
                    Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{' '}
                    <strong>{Math.min(currentPage * itemsPerPage, sortedCampaigns.length)}</strong> of{' '}
                    <strong>{sortedCampaigns.length}</strong> campaigns
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm rounded border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm rounded border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 text-sm rounded ${
                              currentPage === pageNum
                                ? 'bg-primary-600 text-white'
                                : 'border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm rounded border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm rounded border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Industry Benchmarks */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">
            📊 Industry Benchmarks (Healthcare)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Open Rate</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">21.7%</p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Click Rate</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">2.6%</p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">CTOR</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">12%</p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Bounce Rate</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">0.5%</p>
            </div>
          </div>
          <p className="text-xs text-secondary-500 mt-4">
            *Benchmarks based on 2024 healthcare industry averages (Mailchimp, Campaign Monitor)
          </p>
        </div>
      </div>

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <CampaignDetailsModal
          campaign={selectedCampaign}
          companyId={companyId!}
          dataSourceId={dataSourceIds[0]}
          onClose={() => setSelectedCampaign(null)}
        />
      )}
    </div>
  );
};

export default EmailCampaignsPage;
